import './ListPage.scss';
import { SimpleNumberCard, Checkbox, Button, Icon } from '@design-system/index';
import {
    AddDashed,
    ProductsCatalogLine,
    RoundedArrow,
    PoubelleLine,
    PoubelleFull,
    DupliquerLine,
    DupliquerFull,
    SyncroError
} from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ProductTable } from '@services/product/product/product.types';
import { ColumnDef } from '@tanstack/react-table';
import { useGetProductsMutation } from '@services/product/product/product.endpoints';
import { LargeIconButton } from '@design-system/components/Buttons/LargeIconButton/LargeIconButton';
import { CATEGORY_TYPE } from '@utils/enumTranslation';
import { formatMoney } from '@utils/numbers';
import { useListPageVM } from './useListPageVM';
import { ImageLazy } from '@components/ImageLazy/ImageLazy';

export default function ListPage() {
    const {
        totalProducts,
        handleMetadata,
        columnHelper,
        needReload,
        navigate,
        handleToggleProductField,
        handleDeleteProduct,
        handleDuplicateProduct,
        handleDeleteProducts,
        isLoading
    } = useListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('previewImageLink', {
            header: 'Image',
            enableSorting: false,
            size: 0,
            meta: {
                clickable: true
            },
            cell: (info) => <ImageLazy height={52} width={52} src={info.getValue()} />
        }),
        columnHelper.accessor('eticketType.label', {
            header: 'Type E-billet',
            id: 'eticketType',
            enableSorting: true,
            minSize: 160,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),

        columnHelper.accessor('category', {
            header: 'Catégorie',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{CATEGORY_TYPE[info.getValue()]}</>
        }),

        columnHelper.accessor('presentation.title', {
            header: 'Titre',
            id: 'presentation.title',
            enableSorting: true,
            minSize: 250,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),

        columnHelper.accessor('price.priceHT', {
            header: 'Prix',
            id: 'price.priceHT',
            minSize: 180,
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue() ? formatMoney(info.getValue()) : formatMoney(info.row.original.price.priceTTC)}</>
        }),
        columnHelper.accessor('displayManagement.isFeatured', {
            header: 'À la une',
            id: 'displayManagement.isFeatured',
            enableSorting: true,
            minSize: 120,
            size: 0,
            meta: {
                clickable: false
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox
                        checked={info.getValue()}
                        onChange={() => {
                            handleToggleProductField(info.row.original.id, 'isFeatured', info.getValue());
                        }}
                    />
                </div>
            )
        }),

        columnHelper.accessor('displayManagement.isDisabled', {
            header: 'Désactivé',
            id: 'displayManagement.isDisabled',
            enableSorting: true,
            minSize: 100,
            size: 0,
            meta: {
                clickable: false
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox
                        checked={info.getValue()}
                        onChange={() => {
                            handleToggleProductField(info.row.original.id, 'isDisabled', info.getValue());
                        }}
                    />
                </div>
            )
        }),

        columnHelper.accessor('stockAmount', {
            header: 'Stock',
            size: 0,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor('publicLink', {
            header: 'Voir en ligne',
            meta: {
                clickable: false,
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {info.row.original?.syncroStatus === 'error' ? (
                        <Icon icon={<SyncroError />} size={20} color="error" />
                    ) : (
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => {
                                window.open(info.getValue(), '_blank');
                            }}
                        />
                    )}
                </div>
            )
        }),
        columnHelper.display({
            id: 'actions',
            size: 0,
            header: 'Actions',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<DupliquerFull />} size={20} color="primary" />}
                            icon={<Icon icon={<DupliquerLine />} size={20} color="primary" />}
                            onClick={() => handleDuplicateProduct(info.row.original.id)}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<PoubelleFull />} size={20} color="primary" />}
                            icon={<Icon icon={<PoubelleLine />} size={20} color="primary" />}
                            onClick={() => handleDeleteProduct(info.row.original.id)}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<ProductTable>[];

    return (
        <div className="products-table-container">
            <div className="products-table-container-header">
                <div className="products-table-container-header-left">
                    <SimpleNumberCard labelForSingle={'Produit'} labelForMultiple={'Produits'} value={totalProducts} />
                    <LargeIconButton
                        icon={<AddDashed />}
                        label="Nouveau produit"
                        onClick={() => navigate('/products-management/products/add')}
                    />
                </div>
            </div>
            <MutationTable
                key={'active'}
                needReload={needReload}
                columns={columns}
                mutationHook={useGetProductsMutation}
                onRowClick={(row) => navigate(`/products-management/products/${row.id}/edit`)}
                defaultSorting={{
                    field: 'id',
                    direction: 'desc'
                }}
                forceLoading={isLoading}
                headerTitle={'Liste des produits'}
                headerIcon={<ProductsCatalogLine />}
                onMetadata={handleMetadata}
                filters={[
                    {
                        id: 'category',
                        label: 'Catégorie',
                        defaultComparator: 'includes',
                        type: 'multiSelect',
                        options: Object.entries(CATEGORY_TYPE).map(([key, value]) => ({ label: value, value: key }))
                    },
                    {
                        id: 'displayManagement.isFeatured',
                        label: 'À la une',
                        type: 'boolean'
                    },
                    {
                        id: 'displayManagement.isDisabled',
                        label: 'Désactivé',
                        type: 'boolean'
                    }
                ]}
                footer={{
                    actions: [
                        {
                            label: 'Supprimer',
                            icon: <Icon icon={<PoubelleLine />} size={20} color="primary" />,
                            hoverIcon: <Icon icon={<PoubelleFull />} size={20} color="primary" />,
                            onClick: (lines: ProductTable[]) => handleDeleteProducts(lines)
                        }
                    ]
                }}
            />
        </div>
    );
}
